import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import PresignedImage from '../common/PresignedImage';
import {
  isDisplayableImage,
  isLinkAttachment,
  splitFileNameParts,
} from '../../utils/fileUtils';
import { useAttachmentPreview } from './useAttachmentPreview';
import ImageAnnotatorModal from './ImageAnnotatorModal';
import {
  isAttachmentLikedBy,
  likedByIds,
  toggleAttachmentLike,
  toggleLikedByUserIds,
  uploadImageAsAttachment,
  uploadAndReplaceAttachmentImage,
  withPreviousVersion,
} from '../../utils/attachmentMediaApi';
import { updateMessage, sendMessage } from '../../store/slices/chatSlice';
import { addNotification } from '../../store/slices/uiSlice';
import SaveEditedImageDialog from './SaveEditedImageDialog';
import { GoogleDriveIcon } from '../google/DriveFolderNameModal';
import { upsertRealmDocs } from '../../realm';

const GROUP_OPTIONS = [
  { id: 'none', label: 'No group' },
  { id: 'new', label: 'New group' },
  { id: 'existing', label: 'Existing group' },
];

const isImageAtt = (attachment) =>
  attachment?.fileType === 'IMAGE' || isDisplayableImage(attachment);

const AttachmentComposeModal = ({
  attachments = [],
  groupName = '',
  onGroupNameChange,
  groupMode = 'none',
  onGroupModeChange,
  existingGroupId = '',
  onExistingGroupIdChange,
  existingGroups = [],
  loadingExistingGroups = false,
  onClose,
  onReorder,
  onRemove,
  onBrowseAdd,
  onRename,
  readOnly = false,
  initialIndex = 0,
  onEditedImage,
  onSaveToDrive,
  savingToDrive = false,
  driveUploadDone = 0,
  driveUploadTotal = 0,
}) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const [items, setItems] = useState(attachments);
  const [annotating, setAnnotating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingEditedFile, setPendingEditedFile] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(
    Math.max(0, Math.min(initialIndex, Math.max(attachments.length - 1, 0)))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const groupNameRef = useRef(null);
  const thumbStripRef = useRef(null);
  const renameInputRef = useRef(null);

  const count = items.length;
  const safeIndex = count === 0 ? 0 : Math.min(selectedIndex, count - 1);
  const current = items[safeIndex] || null;
  const { baseName, extension } = splitFileNameParts(current?.fileName || '');
  const {
    kind: previewKind,
    pdfUrl,
    textContent,
    textLoading,
    textError,
  } = useAttachmentPreview(current);

  useEffect(() => {
    setItems(attachments);
  }, [attachments]);

  useEffect(() => {
    if (selectedIndex >= count && count > 0) {
      setSelectedIndex(count - 1);
    }
  }, [count, selectedIndex]);

  useEffect(() => {
    setIsEditing(false);
  }, [safeIndex]);

  useEffect(() => {
    const handleKey = (e) => {
      if (annotating) return;
      if (e.key === 'Escape') {
        if (isEditing) {
          setIsEditing(false);
          return;
        }
        onClose?.();
        return;
      }
      if (isEditing) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedIndex((i) => (count ? (i - 1 + count) % count : 0));
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedIndex((i) => (count ? (i + 1) % count : 0));
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, isEditing, count, annotating]);

  useEffect(() => {
    const active = thumbStripRef.current?.querySelector('[data-active="true"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [safeIndex]);

  const startRename = () => {
    setEditName(baseName);
    setIsEditing(true);
    window.requestAnimationFrame(() => renameInputRef.current?.focus());
  };

  const saveRename = () => {
    const trimmed = editName.trim();
    if (!trimmed || !current) {
      setIsEditing(false);
      return;
    }
    onRename?.(safeIndex, trimmed);
    setIsEditing(false);
  };

  const applyMessageUpdate = (message) => {
    if (!message?.id) return;
    const channelId = message.channelId || current?.channelId;
    if (channelId) {
      dispatch(updateMessage({
        channelId,
        message: message.channelId ? message : { ...message, channelId },
      }));
    }
    upsertRealmDocs('Message', [message]);
    const nextAtt = (message.attachments || []).find(
      (item) =>
        (current?.id && item.id === current.id)
        || (current?.fileUrl && item.fileUrl === current.fileUrl)
        || (current?.previousFileUrl && item.previousFileUrl === current.previousFileUrl)
    );
    if (!nextAtt) return;
    setItems((prev) =>
      prev.map((item, idx) => (
        idx === safeIndex
          ? {
              ...item,
              ...nextAtt,
              messageId: item.messageId || message.id,
              channelId: item.channelId || message.channelId,
              file: undefined,
            }
          : item
      ))
    );
  };

  const handleToggleLike = async () => {
    if (!current) return;
    if (currentUserId) {
      setItems((prev) =>
        prev.map((item, idx) => (
          idx === safeIndex
            ? { ...item, likedByUserIds: toggleLikedByUserIds(item.likedByUserIds, currentUserId) }
            : item
        ))
      );
    }
    if (!current.messageId || !current.id) return;
    try {
      const message = await toggleAttachmentLike(current.messageId, current.id);
      applyMessageUpdate(message);
    } catch (error) {
      if (currentUserId) {
        setItems((prev) =>
          prev.map((item, idx) => (
            idx === safeIndex
              ? { ...item, likedByUserIds: toggleLikedByUserIds(item.likedByUserIds, currentUserId) }
              : item
          ))
        );
      }
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data || error?.message || 'Could not like this file',
      }));
    }
  };

  const imageOwnerId = current?.senderId;
  const canOverrideImage = !imageOwnerId || String(imageOwnerId) === String(currentUserId);

  const postEditedImageAsNew = async (file) => {
    const channelId = current.channelId;
    if (!channelId) {
      throw new Error('Could not post edited image to this chat');
    }
    const uploaded = await uploadImageAsAttachment(file, current);
    await dispatch(sendMessage({
      content: '',
      channelId,
      senderId: currentUserId,
      attachments: [uploaded],
      replyToMessageId: current.replyToMessageId || current.messageId || undefined,
    })).unwrap();
    setAnnotating(false);
    setPendingEditedFile(null);
    onClose?.();
    dispatch(addNotification({
      type: 'success',
      message: 'Edited image posted to chat',
    }));
  };

  const handleSaveEditedImage = async (file) => {
    if (!current) return;
    if (onEditedImage && !readOnly) {
      setSavingEdit(true);
      try {
        await onEditedImage(current, file, safeIndex);
        const previewUrl = URL.createObjectURL(file);
        setItems((prev) =>
          prev.map((item, idx) => (
            idx === safeIndex
              ? withPreviousVersion(item, file, previewUrl)
              : item
          ))
        );
        setAnnotating(false);
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: error?.response?.data || error?.message || 'Could not save edited image',
        }));
      } finally {
        setSavingEdit(false);
      }
      return;
    }

    if (current.messageId && current.id) {
      setPendingEditedFile(file);
      return;
    }

    setSavingEdit(true);
    try {
      await postEditedImageAsNew(file);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data || error?.message || 'Could not save edited image',
      }));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmEditedImageSave = async (mode) => {
    const file = pendingEditedFile;
    if (!file || !current) return;
    setSavingEdit(true);
    try {
      if (mode === 'override') {
        const message = await uploadAndReplaceAttachmentImage({
          messageId: current.messageId,
          attachmentId: current.id,
          file,
          previousAttachment: current,
        });
        applyMessageUpdate(message);
        setAnnotating(false);
        setPendingEditedFile(null);
        dispatch(addNotification({
          type: 'success',
          message: 'Edited image saved',
        }));
      } else {
        await postEditedImageAsNew(file);
      }
    } catch (error) {
      const raw = error?.response?.data;
      const message = typeof raw === 'string'
        ? raw
        : (raw?.error || error?.message || 'Could not save edited image');
      dispatch(addNotification({ type: 'error', message }));
    } finally {
      setSavingEdit(false);
    }
  };

  const goPrev = () => setSelectedIndex((i) => (count ? (i - 1 + count) % count : 0));
  const goNext = () => setSelectedIndex((i) => (count ? (i + 1) % count : 0));

  const canEdit = !readOnly;
  const canReorder = canEdit && !!onReorder;

  const handleThumbDragStart = (e, index) => {
    if (!canReorder) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    setDragIndex(index);
  };

  const handleThumbDrop = (e, index) => {
    e.preventDefault();
    const from = dragIndex ?? Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isNaN(from) && from !== index) {
      onReorder?.(from, index);
      setSelectedIndex(index);
    }
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!current) return null;

  return ReactDOM.createPortal(
    <>
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={readOnly ? (groupName || 'Image group') : 'Arrange attachments'}
        className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0 bg-white">
          <div className="min-w-0 flex-1 pr-2">
            {isEditing ? (
              <div className="flex items-center gap-1.5 max-w-lg">
                <div className="flex flex-1 min-w-0 items-center border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                  <input
                    ref={renameInputRef}
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 min-w-0 px-2.5 py-1.5 text-sm text-gray-900 bg-transparent focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveRename();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsEditing(false);
                      }
                    }}
                  />
                  {extension ? (
                    <span className="flex-shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-100 border-l border-gray-200">
                      {extension.replace(/^\./, '')}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={saveRename}
                  className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                  title="Save name"
                  aria-label="Save file name"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
                  title="Cancel"
                  aria-label="Cancel rename"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-900 truncate" title={current.fileName}>
                {current.fileName || 'Attachment'}
              </p>
            )}
            {!isEditing && (
              <p className="text-xs text-gray-500 mt-0.5">
                {readOnly && groupName ? `${groupName} · ` : ''}
                {safeIndex + 1} of {count}
              </p>
            )}
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            {current?.messageId && current?.id && (
              <button
                type="button"
                onClick={handleToggleLike}
                className={`p-2 rounded-full ${
                  isAttachmentLikedBy(current, currentUserId)
                    ? 'bg-red-50 text-red-500'
                    : 'text-gray-500 hover:text-red-500 hover:bg-red-50'
                }`}
                aria-label={isAttachmentLikedBy(current, currentUserId) ? 'Unlike' : 'Like'}
                title={isAttachmentLikedBy(current, currentUserId) ? 'Unlike' : 'Like'}
              >
                <span className="inline-flex items-center gap-1">
                  {isAttachmentLikedBy(current, currentUserId) ? (
                    <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )}
                  {likedByIds(current).length > 0 && (
                    <span className="text-[11px] font-semibold tabular-nums">{likedByIds(current).length}</span>
                  )}
                </span>
              </button>
            )}
            {isImageAtt(current) && (
              <button
                type="button"
                onClick={() => setAnnotating(true)}
                className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full"
                aria-label="Edit image"
                title="Edit image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            )}
            {canEdit && onRename && (
            <button
              type="button"
              onClick={startRename}
              className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full"
              aria-label="Rename file"
              title="Rename"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            )}
            {canEdit && onRemove && (
            <button
              type="button"
              onClick={() => onRemove?.(safeIndex)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full"
              aria-label="Remove file"
              title="Remove"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {canEdit && (
        <div className="px-4 pt-3 pb-2 shrink-0 border-b border-gray-100 bg-gray-50">
          <div
            className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-white border border-gray-200"
            role="radiogroup"
            aria-label="Group organization"
          >
            {GROUP_OPTIONS.map((option) => {
              const selected = groupMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onGroupModeChange?.(option.id)}
                  className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition-all duration-200 ${
                    selected
                      ? 'bg-primary-50 text-primary-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div
            className={`grid transition-[grid-template-rows] duration-300 ease-out ${
              groupMode === 'new' || groupMode === 'existing' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className="overflow-hidden">
              {groupMode === 'new' && (
                <label className="block pt-2.5">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Group name</span>
                  <input
                    ref={groupNameRef}
                    type="text"
                    value={groupName}
                    onChange={(e) => onGroupNameChange?.(e.target.value)}
                    placeholder="e.g. Site photos"
                    maxLength={80}
                    className="w-full text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500"
                  />
                </label>
              )}
              {groupMode === 'existing' && (
                <label className="block pt-2.5">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Add to group</span>
                  <select
                    value={existingGroupId}
                    onChange={(e) => onExistingGroupIdChange?.(e.target.value)}
                    disabled={loadingExistingGroups}
                    className="w-full text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500 disabled:opacity-60"
                  >
                    <option value="">
                      {loadingExistingGroups ? 'Loading groups…' : 'Select a group'}
                    </option>
                    {existingGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.fileCount} item{group.fileCount === 1 ? '' : 's'})
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="relative flex-1 flex items-center justify-center min-h-0 bg-gray-100 px-12 sm:px-16 py-4">
          {count > 1 && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 text-gray-700 bg-white hover:bg-gray-50 rounded-full border border-gray-200 shadow-md"
              aria-label="Previous"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {isImageAtt(current) ? (
            <PresignedImage
              key={current.fileUrl || safeIndex}
              fileKey={current.fileUrl}
              alt={current.fileName}
              originalName={current.fileName}
              className="max-w-full max-h-full object-contain select-none rounded-lg shadow-sm"
              loadingClassName="min-h-[200px] min-w-[200px] rounded-lg bg-gray-200 animate-pulse"
            />
          ) : previewKind === 'pdf' ? (
            <div className="w-full max-w-4xl h-full max-h-full rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm">
              {pdfUrl ? (
                <iframe
                  title={current.fileName}
                  src={`${pdfUrl}#page=1&toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                  className="w-full h-full min-h-[320px] border-0 bg-white"
                />
              ) : (
                <div className="w-full min-h-[280px] flex items-center justify-center text-sm text-gray-400">
                  Loading PDF…
                </div>
              )}
            </div>
          ) : previewKind === 'text' || previewKind === 'docx' ? (
            <div className="w-full max-w-4xl max-h-full rounded-lg border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden">
              {previewKind === 'docx' && !textLoading && !textError && (
                <p className="shrink-0 px-4 pt-3 text-[11px] text-gray-400">
                  Text preview of Word document — layout and images are not shown
                </p>
              )}
              <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
                {textLoading && (
                  <div className="flex items-center justify-center min-h-[200px] text-sm text-gray-400">
                    {previewKind === 'docx' ? 'Loading document…' : 'Loading text…'}
                  </div>
                )}
                {!textLoading && textError && (
                  <div className="flex items-center justify-center min-h-[200px] text-sm text-gray-500 text-center px-4">
                    {textError}
                  </div>
                )}
                {!textLoading && !textError && textContent != null && (
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {textContent}
                  </pre>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-gray-500">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm font-medium truncate max-w-xs">{current.fileName}</p>
              <p className="mt-1 text-xs text-gray-400">Preview not available for this file type</p>
            </div>
          )}

          {count > 1 && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 text-gray-700 bg-white hover:bg-gray-50 rounded-full border border-gray-200 shadow-md"
              aria-label="Next"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-500">
              {canReorder ? 'All items · drag to change sequence' : 'All items'}
            </p>
            <div className="flex items-center gap-2">
              {canEdit && onSaveToDrive && items.some((item) => item?.fileUrl && !isLinkAttachment(item)) && (
                <button
                  type="button"
                  onClick={() => onSaveToDrive?.()}
                  disabled={savingToDrive}
                  className="px-3 py-1 rounded-lg text-xs font-semibold border border-primary-200 text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <GoogleDriveIcon className="w-4 h-4" />
                    {savingToDrive && driveUploadTotal > 0
                      ? `${driveUploadDone}/${driveUploadTotal} uploaded`
                      : savingToDrive
                        ? 'Saving to Drive…'
                        : 'Save to Drive folder'}
                  </span>
                </button>
              )}
              {canEdit && onBrowseAdd && (
              <button
                type="button"
                onClick={() => onBrowseAdd?.()}
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Add more
              </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-primary-600 hover:bg-primary-700 text-white"
              >
                Done
              </button>
            </div>
          </div>
          <div
            ref={thumbStripRef}
            className="flex gap-2 overflow-x-auto py-1"
          >
            {items.map((item, idx) => {
              const isActive = idx === safeIndex;
              return (
                <button
                  key={item.id || item.fileUrl || idx}
                  type="button"
                  data-active={isActive ? 'true' : 'false'}
                  draggable={canReorder}
                  onDragStart={canReorder ? (e) => handleThumbDragStart(e, idx) : undefined}
                  onDragOver={canReorder ? (e) => {
                    e.preventDefault();
                    if (dragIndex !== null && dragIndex !== idx) setOverIndex(idx);
                  } : undefined}
                  onDragEnd={canReorder ? () => {
                    setDragIndex(null);
                    setOverIndex(null);
                  } : undefined}
                  onDrop={canReorder ? (e) => handleThumbDrop(e, idx) : undefined}
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedIndex(idx);
                  }}
                  className={`relative flex-shrink-0 w-[72px] h-[72px] rounded-xl overflow-hidden transition-all ${
                    canReorder ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                  } ${
                    isActive
                      ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-white'
                      : overIndex === idx
                        ? 'ring-2 ring-primary-300'
                        : 'opacity-80 hover:opacity-100'
                  } ${dragIndex === idx ? 'opacity-40' : ''}`}
                  aria-label={`View item ${idx + 1}: ${item.fileName || ''}`}
                  aria-current={isActive}
                >
                  {isImageAtt(item) ? (
                    <PresignedImage
                      fileKey={item.fileUrl}
                      alt={item.fileName}
                      originalName={item.fileName}
                      className="w-full h-full object-cover pointer-events-none"
                      loadingClassName="w-full h-full bg-gray-200 animate-pulse"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-[10px] font-semibold text-gray-500 px-1 text-center">
                      {item.fileName || 'File'}
                    </div>
                  )}
                  <span className="absolute bottom-1 left-1 min-w-[18px] h-[18px] px-1 rounded bg-white/90 text-[10px] font-semibold text-gray-800 leading-[18px] text-center shadow-sm">
                    {idx + 1}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    {annotating && current && (
      <ImageAnnotatorModal
        attachment={current}
        onClose={() => {
          if (savingEdit) return;
          setPendingEditedFile(null);
          setAnnotating(false);
        }}
        onSave={handleSaveEditedImage}
        saving={savingEdit && !pendingEditedFile}
      />
    )}
    {pendingEditedFile && (
      <SaveEditedImageDialog
        canOverride={canOverrideImage}
        saving={savingEdit}
        onOverride={() => confirmEditedImageSave('override')}
        onSaveNew={() => confirmEditedImageSave('new')}
        onCancel={() => {
          if (savingEdit) return;
          setPendingEditedFile(null);
        }}
      />
    )}
    </>,
    document.body
  );
};

export default AttachmentComposeModal;
